import { SharedSubjectService } from './../../../shared/services/shared-subject.service';
import { SnackbarService } from 'src/app/shared/services/snackBarService.service';
import { FolderApiService } from 'src/app/folder/services/folder-api.service';
import { MatDialog } from '@angular/material/dialog';
import { CreateFolderComponent } from './../../../folder/create-folder/create-folder.component';
import { animate, state, style, transition, trigger } from '@angular/animations';
import { Component, EventEmitter, Input, OnInit, Output, ChangeDetectionStrategy } from '@angular/core';
import { Router, ActivatedRoute } from '@angular/router';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { takeUntil } from 'rxjs/operators';
import isFunction from 'lodash-es/isFunction';
import { SidenavItem, eNavItemType } from 'src/app/shared/interfaces/sidenav-item.interface';
import { SidenavService } from 'src/app/shared/services/sidenav.service';
import { AlertDialogComponent } from 'src/app/shared/components/alert-dialog/alert-dialog.component';
import { ApiResponse } from 'src/app/shared/models/api.response';
import { GlobalService } from 'src/app/shared/services/global.service';
import { FlatTreeControl } from '@angular/cdk/tree';
import { MatTreeFlatDataSource, MatTreeFlattener } from '@angular/material/tree';
import { AuthService } from 'src/app/shared/services/auth.service';
import { DocumentApiService } from 'src/app/folder/services/document-api.service';
import { BaseComponent } from 'src/app/core/base-classes/base/base.component';

interface ArchiveFolderList {
  id: string;
  name: string;
  subItems?: ArchiveFolderList[];
  url: string;
  itemType: eNavItemType;
}

interface TreeFlatNode {
  expandable: boolean;
  name: string;
  level: number;
  url: string;
  id: string;
  itemType: eNavItemType;
}

@Component({
    selector: 'fury-sidenav-item',
    templateUrl: './sidenav-item.component.html',
    styleUrls: ['./sidenav-item.component.scss'],
    animations: [
        trigger('dropdownOpen', [
            state('false', style({
                height: 0
            })),
            state('true', style({
                height: '*'
            })),
            transition('false <=> true', animate('300ms cubic-bezier(.35, 0, .25, 1)'))
        ])
    ],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class SidenavItemComponent extends BaseComponent implements OnInit {

  @Input('item') public item: SidenavItem;
  @Input('level') public level: number;
  @Output() public isDeleted = new EventEmitter<void>();

  public isCollapsed$ = this._sidenavService.collapsed$;
  public dropdownOpen$: Observable<boolean>;
  public folderId: string | null = null;
  public folderMenu: boolean = false;
  public isSelectedMap: Map<string, boolean> = new Map<string, boolean>();
  public deleteArchiveFolderMessage = 'Are you sure you want to delete this Folder, deleting the Folder will delete all Archive Audits and nested Folders too?';
  public deleteDocumentFolderMessage = 'Are you sure you want to delete this Folder, deleting the Folder will delete all Attestation files and nested Folders too?';
  public otherFunction: boolean = false;
  public isLastChild: boolean;
  public eNavItemType = eNavItemType;

  private _transformer = (node: ArchiveFolderList, level: number) => {
    this.isLastChild = node.subItems === undefined || node.subItems.length === 0;
    return {
      expandable: !this.isLastChild,
      name: node.name,
      url: node.url,
      id: node.id,
      level: level,
      itemType: node.itemType,
      paddingIndent: 20,   // 20px per level — same for all nodes so CDK multiplies level × 20
    };
  }

  public treeControl = new FlatTreeControl<TreeFlatNode>(
    node => node.level, node => node.expandable);

  public treeFlattener = new MatTreeFlattener(
    this._transformer, node => node.level, node => node.expandable, node => node.subItems);

  public archivesDataSource = new MatTreeFlatDataSource(this.treeControl, this.treeFlattener);
  public documentsDataSource = new MatTreeFlatDataSource(this.treeControl, this.treeFlattener);

  constructor(
    private _sidenavService: SidenavService,
    private _dialog: MatDialog,
    private _router: Router,
    private _folderService: FolderApiService,
    private _documentFolderService: DocumentApiService,
    private _snackbarService: SnackbarService,
    private _globalService: GlobalService,
    private _sharedSubjectService: SharedSubjectService,
    private _authService: AuthService
  ) {
    super();
    this._router.events
      .pipe(takeUntil(this.destroy$))
      .subscribe((val) => {
        const navEvent = val as unknown as { url?: string };
        if (navEvent?.url?.includes(this.item.routeOrFunction as string) && this.item.routeOrFunction != '/' && navEvent?.url?.includes('?')) {
          queueMicrotask(() => {
            this._sidenavService.toggleItemOpen(this.item);
          });
        }
      });

    this.dropdownOpen$ = this._sidenavService.currentlyOpen$.pipe(
      map(currentlyOpen => this.item.subItems && this.item.subItems.length > 0 && currentlyOpen.indexOf(this.item) > -1)
    );
  }

  get levelClass() {
    return `level-${this.level}`;
  }

  ngOnInit() {

    if (this.item.itemType == eNavItemType.archives && this._authService.isInRole(['Admin'])) {
      this.getArchiveFolder();
    }

    if (this.item.itemType == eNavItemType.documents && this._authService.isInRole(['Admin'])) {
      this.getDocumentFolders();
    }

    // The layout rebuilds the sidenav items after a folder is added / edited / deleted,
    // but their track keys are unchanged, so this component is reused and ngOnInit does
    // not run again. Reload the list here so the change shows without a page refresh.
    this._sharedSubjectService.folderRefresh
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        if (!this._authService.isInRole(['Admin'])) {
          return;
        }
        if (this.item.itemType == eNavItemType.archives) {
          this.getArchiveFolder();
        } else if (this.item.itemType == eNavItemType.documents) {
          this.getDocumentFolders();
        }
      });

    if (this._router.url?.includes(this.item.routeOrFunction as string) && this.item.routeOrFunction != '/') {
      queueMicrotask(() => {
        this._sidenavService.toggleItemOpen(this.item);
      });
    }
  }

  getRouterLinkActive(item: SidenavItem) {
    if ((this._router?.url == item.routeOrFunction && item.routeOrFunction != '/') ||
      (this._router.url?.includes('?') && this._router.url?.includes(this.item.routeOrFunction as string) && item.routeOrFunction != '/')) {
      return false;
    } else {
      return item.pathMatchExact;
    }
  }

  addFolder(type: string, itemType: eNavItemType) {
    // No width override — CreateFolderComponent's own .modal-dialog-container
    // (min-width: 480px, max-width: 560px) sizes the dialog. A wider fixed
    // width here (e.g. 45vw) left a lot of blank space on the right on
    // large screens once the content hit its own max-width cap.
    this._dialog.open(CreateFolderComponent, {
      data: {
        pageType: type,
        itemType
      }
    })
  }

  editFolder(type: string, name: string, id: string, itemType: string) {
    this._dialog.open(CreateFolderComponent, {
      data: {
        pageType: type,
        folderName: name,
        folderId: id,
        itemType: eNavItemType[itemType]
      }

    })
  }


  deleteFolder(id: string, itemType: eNavItemType) {
    this._dialog.open(AlertDialogComponent, {
      width: '25vw',
      maxHeight: '100vh',
      data: {
        title: 'Delete',
        message: itemType === eNavItemType.archives ? this.deleteArchiveFolderMessage : this.deleteDocumentFolderMessage,
        buttons: 'delete'
      }
    }).afterClosed()
      .pipe(takeUntil(this.destroy$))
      .subscribe((res: boolean) => {
        if (res == true) {
          let req = {
            folderId: id,
          }
          const folderService = itemType === eNavItemType.archives
            ? this._folderService
            : this._documentFolderService;

          folderService.deleteFolder(req)
            .pipe(takeUntil(this.destroy$))
            .subscribe((res: ApiResponse<unknown>) => {
              this._snackbarService.open(res.message, 'Close', 'success')
              // Navigate first: a navigation cancels in-flight GETs, which would also cancel
              // the list reload this refresh triggers.
              this._router.navigateByUrl('/').then(() => this._sharedSubjectService.folderRefresh.next(''));

            });
        }
      });
  }

  isFunction(routeOrFunction: string[] | Function) {
    return isFunction(routeOrFunction);
  }

  handleClick() {

    if (this.item.subItems && this.item.subItems.length > 0) {
      this._sidenavService.toggleItemOpen(this.item);
    } else if ((typeof this.item.routeOrFunction === 'string' || this.item.routeOrFunction instanceof String)) {
      this._router.navigate([this.item.routeOrFunction]);
    } else if (typeof this.item.routeOrFunction === 'function' || this.item.routeOrFunction instanceof Function) {
      this.item.routeOrFunction();
    } else {
      throw Error('Could not determine what to do, Sidenav-Item has no routeOrFunction set AND does not contain any subItems');
    }
  }



  navigationOpen(url: string, nodeId: string | null) {
    this._router.navigateByUrl(url);
  }

  getArchiveFolder() {

    const req = {
      companyId: this._globalService.company.id,
      offset: 0,
      limit: 50,
    };
    this._folderService.getArchivedFolder(req)
      .pipe(takeUntil(this.destroy$))
      .subscribe((res: ApiResponse<ArchiveFolderList[]>) => {
        this.archivesDataSource.data = res.data;
      });
  }

  getDocumentFolders() {

    const req = {
      companyId: this._globalService.company.id,
      offset: 0,
      limit: 50,
    };
    this._documentFolderService.getPdfDocumentFolders(req)
      .pipe(takeUntil(this.destroy$))
      .subscribe((res: ApiResponse<ArchiveFolderList[]>) => {
        this.documentsDataSource.data = res.data;
      });
  }

  hasChild = (_: number, node: TreeFlatNode) => node.expandable;

  getTextIcon(item: SidenavItem) {
    let result = '';

    if (item) {
      const name = item.name.split(' ');

      if (name.length > 0) {
        result += name[0].charAt(0).toUpperCase();
      }

      if (name.length > 1) {
        result += name[1].charAt(0).toLowerCase();
      }

      if (name.length === 1) {
        result += name[0].charAt(1).toLowerCase();
      }
    }

    return result;
  }
}
